import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import screenfull from 'screenfull';
import { IS_ANNOTATION } from 'store/actions';
import ImageAnnotationTool from '../videoPage/ImageAnnotationTool';
import RightBar from '../videoPage/RightBar';
import DatePickerStore from '../Calendar';
import EvidenceVideo from './EvidenceVideo';
import './video.css';

// const BASE = 'https://db.neophyte.live/fm/videos';

const EvidencePage = () => {
  const dispatch = useDispatch();

  const isAnnotation = useSelector((s) => s.cameraReducer.isAnnotation);
  const checkImage = useSelector((s) => s.cameraReducer.checkImage);
  const currentDate = useSelector((s) => s.customization.currentDate);
  const selectedCamera = useSelector((s) => s.cameraReducer.cameraData);
  const selectedVideoSource = useSelector((s) => s.customization.videoSource);

  const [evidenceData, setEvidenceData] = useState([]);
  const [source, setSource] = useState('');
  const [duration, setDuration] = useState(0);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);

  // Debug logging
  console.log('Evidence component - current source:', source);
  console.log('Evidence component - selectedVideoSource from Redux:', selectedVideoSource);
  const [currentTime, setCurrentTime] = useState(0);
  const [muted, setMuted] = useState(true);
  const [volume, setVolume] = useState(0.6);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const playerRef = useRef(null); // EvidenceVideo imperative handle
  const frameRef = useRef(null); // for fullscreen container

  // turn on annotation mode if an image is selected
  useEffect(() => {
    if (checkImage) dispatch({ type: IS_ANNOTATION, isAnnotation: true });
  }, [checkImage, dispatch]);

  // fetch evidence data from API
  const fetchEvidenceData = async (date, device) => {
    try {
      const r = await fetch('https://db.neophyte.live/fm-api/kpiCreation/evidenceList', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date, device })
      });
      const data = await r.json();
      
      // Set evidence data for timeline visualization
      setEvidenceData(Array.isArray(data) ? data : []);
      
      // Reset video index and set first video as source if available
      setCurrentVideoIndex(0);
      if (data?.[0]?.videos) setSource(`${data[0].videos}`);
    } catch (error) {
      console.error('Error fetching evidence data:', error);
      setEvidenceData([]);
    }
  };

  // fetch graph points
  // const fetchGraphData = async (date, device) => {
  //   const r = await fetch('https://db.neophyte.live/fm/v2/evidenceVideo', {
  //     method: 'POST',
  //     headers: { 'Content-Type': 'application/json' },
  //     body: JSON.stringify({ date, device })
  //   });
  //   const raw = await r.json();
  //   const points = Array.isArray(raw)
  //     ? raw.flatMap((row) =>
  //         Array.isArray(row.label_wise) ? row.label_wise.map((d) => ({ x: new Date(d.time).getTime(), y: (d.track_ids || []).length })) : []
  //       )
  //     : [];
  //   setGraphData(points);
  // };

  useEffect(() => {
    const date = currentDate;
    const device = selectedCamera?.device_name;
    if (date && device) {
      fetchEvidenceData(date, device);
      // Reset to paused state when new video loads
      setIsPlaying(false);
    }
  }, [currentDate, selectedCamera?.device_name]);

  // Listen for video source changes from evidence list clicks
  useEffect(() => {
    if (selectedVideoSource) {
      console.log('Setting video source:', selectedVideoSource);
      setSource(selectedVideoSource);
    }
  }, [selectedVideoSource]);

  // computed
  const progressPct = useMemo(() => (duration ? Math.min(100, (currentTime / duration) * 100) : 0), [currentTime, duration]);
  const fmt = useCallback((sec = 0) => {
    const totalMinutes = Math.floor(sec / 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const seconds = Math.floor(sec % 60);

    return `${String(hours).padStart(2, '0')}.${String(minutes).padStart(2, '0')}.${String(seconds).padStart(2, '0')}`;
  }, []);

  // Helper function to determine device type based on device ID
  const getDeviceType = (deviceId) => {
    if (!deviceId) return 'unknown';
    
    // Convert to uppercase for consistent matching
    const device = deviceId.toString().toUpperCase();
    
    // Check for truck devices (D014, D012, etc.)
    if (device.startsWith('D0') && (device.includes('14') || device.includes('12'))) {
      return 'truck';
    }
    
    // Check for packing devices (D017, etc.)
    if (device.startsWith('D0') && device.includes('17')) {
      return 'packing';
    }
    
    // Default fallback based on evidence type
    return 'safety'; // Keep existing safety type as fallback
  };

  // Convert evidence data to timeline markers
  const evidenceMarkers = useMemo(() => {
    if (!evidenceData.length || !duration) return [];
    
    // Get start of day for the current date
    const dayStart = new Date(currentDate);
    dayStart.setHours(0, 0, 0, 0);
    
    return evidenceData.map((evidence, index) => {
      const startTime = new Date(evidence.start_time);
      const endTime = new Date(evidence.end_time);
      
      // Calculate seconds from start of day
      const startSeconds = (startTime.getTime() - dayStart.getTime()) / 1000;
      const endSeconds = (endTime.getTime() - dayStart.getTime()) / 1000;
      
      // Convert to percentage of 24 hours (86400 seconds)
      const startPercent = (startSeconds / 86400) * 100;
      const endPercent = (endSeconds / 86400) * 100;
      const widthPercent = endPercent - startPercent;
      
      // Determine device type based on device ID
      const deviceType = getDeviceType(evidence.device);
      
      return {
        id: index,
        type: evidence.type,
        deviceType: deviceType,
        instance: evidence.instance,
        device: evidence.device,
        startPercent: Math.max(0, Math.min(100, startPercent)),
        widthPercent: Math.max(0.1, Math.min(100 - startPercent, widthPercent)),
        startTime: evidence.start_time,
        endTime: evidence.end_time,
        videos: evidence.videos,
        imageUrl: evidence.image_url,
        ppe: evidence.ppe,
        shoes: evidence.shoes
      };
    });
  }, [evidenceData, duration, currentDate]);

  // waveform (blue bars) - using hardcoded dummy data
  const waveform = useMemo(() => {
    // Generate dummy data that matches the image pattern
    const dummyData = [];
    for (let i = 0; i < 120; i++) {
      // Create varying heights that simulate evidence activity
      let height;
      if (i < 20) height = Math.random() * 15 + 5; // Low activity start
      else if (i < 40) height = Math.random() * 35 + 15; // Medium activity
      else if (i < 60) height = Math.random() * 50 + 20; // High activity
      else if (i < 80) height = Math.random() * 25 + 10; // Medium activity
      else height = Math.random() * 15 + 5; // Low activity end

      dummyData.push(Math.min(60, height));
    }
    return dummyData;
  }, []);

  // evidenceVideo callbacks
  const handleEnded = () => {
    setIsPlaying(false);
    // Reset video to beginning for replay functionality
    const p = playerRef.current;
    if (p) {
      p.currentTime(0);
      setCurrentTime(0);
    }
  };
  const handleTime = (t) => setCurrentTime(t || 0);
  const handleMeta = (d) => setDuration(d || 0);
  const handlePlay = () => setIsPlaying(true);
  const handlePause = () => setIsPlaying(false);

  // transport & UI handlers
  const togglePlay = () => {
    const p = playerRef.current;
    if (!p) return;
    if (p.paused()) {
      p.play();
      setIsPlaying(true);
    } else {
      p.pause();
      setIsPlaying(false);
    }
  };
  // const seekRelative = (delta) => {
  //   const p = playerRef.current;
  //   if (!p) return;
  //   const t = Math.max(0, Math.min((p.currentTime() || 0) + delta, p.duration() || 0));
  //   p.currentTime(t);
  //   setCurrentTime(t);
  // };

  // Navigate to previous video
  const goToPreviousVideo = () => {
    if (!evidenceData.length) return;
    const prevIndex = currentVideoIndex > 0 ? currentVideoIndex - 1 : evidenceData.length - 1;
    setCurrentVideoIndex(prevIndex);
    if (evidenceData[prevIndex]?.videos) {
      setSource(`${evidenceData[prevIndex].videos}`);
      setCurrentTime(0);
      setIsPlaying(false);
    }
  };

  // Navigate to next video
  const goToNextVideo = () => {
    if (!evidenceData.length) return;
    const nextIndex = currentVideoIndex < evidenceData.length - 1 ? currentVideoIndex + 1 : 0;
    setCurrentVideoIndex(nextIndex);
    if (evidenceData[nextIndex]?.videos) {
      setSource(`${evidenceData[nextIndex].videos}`);
      setCurrentTime(0);
      setIsPlaying(false);
    }
  };

  const onScrub = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1);
    const t = pct * (duration || 0);
    playerRef.current?.currentTime(t);
    setCurrentTime(t);
  };
  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    playerRef.current?.muted(next);
  };
  // const onVol = (e) => {
  //   const v = parseFloat(e.target.value);
  //   setVolume(v);
  //   if (v > 0 && muted) setMuted(false);
  //   playerRef.current?.volume(v);
  //   if (v > 0) playerRef.current?.muted(false);
  // };
  const onVolumeScrub = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1);
    const newVolume = pct;
    setVolume(newVolume);
    if (newVolume > 0 && muted) setMuted(false);
    playerRef.current?.volume(newVolume);
    if (newVolume > 0) playerRef.current?.muted(false);
  };
  const toggleFullscreen = () => {
    const el = frameRef.current;
    if (el && screenfull.isEnabled) {
      if (screenfull.isFullscreen) {
        screenfull.exit();
        setIsFullscreen(false);
      } else {
        screenfull.request(el);
        setIsFullscreen(true);
      }
    }
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(screenfull.isFullscreen);
    };

    if (screenfull.isEnabled) {
      screenfull.on('change', handleFullscreenChange);
      return () => screenfull.off('change', handleFullscreenChange);
    }
  }, []);

  return (
    <div className="" style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      {/* Header row with Evidence title and date picker */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
          paddingLeft: '8px',
          paddingRight: '8px',
          flexShrink: 0
        }}
      >
        {/* EVIDENCE TITLE */}
        <h1
          style={{
            fontFamily: 'Plus Jakarta Sans',
            fontSize: '28px',
            fontWeight: 600,
            margin: 0,
            background: 'linear-gradient(135deg,#fff 0%,rgba(255,255,255,.85) 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            letterSpacing: '-.025em',
            lineHeight: 1.2,
            marginLeft: 12
          }}
        >
          Evidence
        </h1>

        {/* Date picker */}
        <DatePickerStore
          sx={{
            '& .MuiInputBase-root': {
              height: 40,
              width: 160,
              fontSize: '0.875rem',
              backgroundColor: '#1f2745',
              color: '#fff',
              borderRadius: '8px',
              border: '1px solid #2a3359'
            },
            '& .MuiSvgIcon-root': { color: '#fff' }
          }}
        />
      </div>

      {/* Main content area with video and right panel */}
      <div style={{ 
        display: 'flex', 
        gap: window.innerWidth > 1440 ? '20px' : '12px', 
        flex: 1, 
        minHeight: 0, 
        padding: '0 8px', 
        marginLeft: '-40px' 
      }}>
        {/* Left side - Video content */}
        <div style={{ 
          flex: '0 0 70%', 
          display: 'flex', 
          flexDirection: 'column', 
          maxWidth: '70%' 
        }}>
          <div className="neo-body">
            {/* <SideBar selField="videos" /> */}

            {/* VIDEO PANEL */}
            <section className="neo-videoplayer" style={{ flex: 1 }}>
              <div ref={frameRef} className="neo-video-frame">
                {isAnnotation ? (
                  <ImageAnnotationTool />
                ) : (
                  <EvidenceVideo
                    ref={playerRef}
                    vdoSource={source}
                    autoPlay={false}
                    muted={muted}
                    onEnded={handleEnded}
                    onTime={handleTime}
                    onLoadedMeta={handleMeta}
                    onPlay={handlePlay}
                    onPause={handlePause}
                  />
                )}

                {/* camera tag (top-left) */}
                <div className="neo-video-tag">
                  <svg xmlns="http://www.w3.org/2000/svg" width="124" height="57" viewBox="0 0 124 57" fill="none">
                    <foreignObject x="-15" y="-15" width="154" height="87">
                      <div
                        xmlns="http://www.w3.org/1999/xhtml"
                        style={{
                          backdropFilter: 'blur(7.5px)',
                          WebkitBackdropFilter: 'blur(7.5px)',
                          clipPath: 'url(#bgblur_0_160_781_clip_path)',
                          height: '100%',
                          width: '100%'
                        }}
                      ></div>
                    </foreignObject>
                    <rect width="124" height="57" rx="28.5" fill="#0F0E0E" fillOpacity="0.3" />
                    <ellipse cx="25.582" cy="28.3467" rx="15.1406" ry="16.7957" fill="white" fillOpacity="0.63" />
                    <ellipse cx="25.5818" cy="28.3466" rx="5.74297" ry="6.37079" fill="#EB5757" />
                    <text
                      x="74"
                      y="28.5"
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fill="#FFF"
                      fontFamily="Inter"
                      fontSize="20"
                      fontWeight="500"
                    >
                      {selectedCamera?.device_name || 'D03'}
                    </text>
                    <defs>
                      <clipPath id="bgblur_0_160_781_clip_path" transform="translate(15 15)">
                        <rect width="124" height="57" rx="28.5" />
                      </clipPath>
                    </defs>
                  </svg>
                </div>

                {/* CUSTOM CONTROLS (YouTube-style layout) */}
                <div className="neo-controls-overlay">
                  {/* Progress Bar at Top */}
                  <div className="neo-progress-top">
                    <div
                      className="neo-progress"
                      onClick={onScrub}
                      role="slider"
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-valuenow={Math.round(progressPct)}
                    >
                      <div className="neo-progress-fill" style={{ width: `${progressPct}%` }} />
                      <div className="neo-thumb" style={{ left: `${progressPct}%` }} />
                    </div>
                  </div>

                  {/* All Other Controls Below */}
                  <div className="neo-controls">
                    {/* Left Controls Group */}
                    <div className="neo-controls-left">
                      {/* Previous/Rewind */}
                      <button className="neo-icon" onClick={goToPreviousVideo} title="Previous Video">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M16 16L4.66667 8L16 0V16ZM2.66667 0H0V16H2.66667V0Z" fill="white" />
                        </svg>
                      </button>

                      {/* Play/Pause */}
                      <button className="neo-icon" onClick={togglePlay} title={isPlaying ? 'Pause' : 'Play'}>
                        {isPlaying ? (
                          <svg xmlns="http://www.w3.org/2000/svg" width="15" height="20" viewBox="0 0 15 20" fill="none">
                            <path d="M0 0.5H5V19.5H0V0.5Z" fill="white" />
                            <path d="M10 0.5H15V19.5H10V0.5Z" fill="white" />
                          </svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="31" viewBox="0 0 24 31" fill="none">
                            <path
                              d="M1.41602 1.99609L22.3529 15.4555L1.41602 28.915V1.99609Z"
                              stroke="white"
                              strokeWidth="2.5637"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              fill="none"
                            />
                          </svg>
                        )}
                      </button>

                      {/* Next/Forward */}
                      <button className="neo-icon" onClick={goToNextVideo} title="Next Video">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M0 16L11.3333 8L0 0V16ZM13.3333 0H16V16H13.3333V0Z" fill="white" />
                        </svg>
                      </button>

                      {/* Volume */}
                      <button className="neo-icon" onClick={toggleMute} title={muted ? 'Unmute' : 'Mute'}>
                        {muted ? (
                          <svg xmlns="http://www.w3.org/2000/svg" width="19" height="20" viewBox="0 0 19 20" fill="none">
                            <path
                              d="M1.41 0.935059L0 2.34506L4.36 6.70506L4.07 7.00506H0.0699999V13.0051H4.07L9.07 18.0051V11.4151L13.25 15.5951C12.6 16.0851 11.87 16.4751 11.07 16.7051V18.7651C12.41 18.4651 13.64 17.8451 14.68 17.0151L16.73 19.0651L18.14 17.6551L1.41 0.935059ZM16.07 10.0051C16.07 10.8251 15.92 11.6151 15.66 12.3451L17.19 13.8751C17.75 12.7051 18.07 11.3951 18.07 10.0051C18.07 5.72506 15.08 2.14506 11.07 1.23506V3.29506C13.96 4.15506 16.07 6.83506 16.07 10.0051ZM9.07 2.00506L7.19 3.88506L9.07 5.76506V2.00506ZM13.57 10.0051C13.57 8.23506 12.55 6.71506 11.07 5.97506V7.76506L13.55 10.2451C13.56 10.1651 13.57 10.0851 13.57 10.0051Z"
                              fill="white"
                            />
                          </svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none">
                            <path
                              d="M10.2917 5.20667L6.01886 8.62494H2.60059V13.7523H6.01886L10.2917 17.1706V5.20667Z"
                              stroke="white"
                              strokeWidth="1.92278"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M17.1885 5.14673C18.7906 6.74928 19.6906 8.92252 19.6906 11.1885C19.6906 13.4545 18.7906 15.6278 17.1885 17.2303M14.1719 8.16335C14.9729 8.96463 15.4229 10.0512 15.4229 11.1843C15.4229 12.3173 14.9729 13.4039 14.1719 14.2051"
                              stroke="white"
                              strokeWidth="1.92278"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </button>

                      {/* Volume Progress Bar */}
                      <div className="neo-volume-progress" onClick={onVolumeScrub}>
                        <div className="neo-volume-bar">
                          <div className="neo-volume-fill" style={{ width: `${volume * 100}%` }} />
                        </div>
                      </div>

                      {/* Current Time / Duration */}
                      <div className="neo-time">
                        {fmt(currentTime)} / {fmt(duration)}
                      </div>
                    </div>

                    {/* Center spacer (no progress bar here anymore) */}
                    <div className="neo-controls-center"></div>

                    {/* Right Controls Group */}
                    <div className="neo-controls-right">
                      {/* Settings */}
                      <button className="neo-icon" title="Settings">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M12 8a4 4 0 100 8 4 4 0 000-8z" stroke="white" strokeWidth="2" />
                          <path
                            d="M19.4 15a8.2 8.2 0 00.2-2 8.2 8.2 0 00-.2-2l2.1-1.6-2-3.4-2.5 1a8.3 8.3 0 00-3.5-2l-.4-2.7H9l-.4 2.7a8.3 8.3 0 00-3.5 2l-2.5 1-2 3.4L2.7 11a8.2 8.2 0 00-.2 2 8.2 8.2 0 00.2 2l-2.1 1.6 2 3.4 2.5-1a8.3 8.3 0 003.5 2l.4 2.7h4l.4-2.7a8.3 8.3 0 003.5-2l2.5 1 2-3.4-2.1-1.6z"
                            stroke="white"
                            strokeWidth="1.5"
                            opacity=".85"
                          />
                        </svg>
                      </button>

                      {/* Fullscreen/Minimize */}
                      <button className="neo-icon" onClick={toggleFullscreen} title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}>
                        {isFullscreen ? (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M9 9L4 4M4 4h5M4 4v5" />
                            <path d="M15 15l5 5M20 20h-5M20 20v-5" />
                            <path d="M20 4l-5 5M20 4h-5M20 4v5" />
                            <path d="M4 20l5-5M4 20h5M4 20v-5" />
                          </svg>
                        ) : (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path d="M8 3H3v5M16 3h5v5M8 21H3v-5M16 21h5v-5" stroke="white" strokeWidth="2" strokeLinecap="round" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* EVIDENCE TIMELINE GRAPH */}
              <div className="evidence-timeline timeline-container">
                {/* Timeline Header */}
                <div className="timeline-header">
                  <div className="timeline-info">
                    <div className="timeline-title">
                      <span className="title-main">Evidence Timeline</span>
                      <span className="evidence-count">({evidenceData.length} events)</span>
                    </div>
                  </div>
                </div>

                {/* Timeline Markers - 24 hour format */}
                <div className="timeline-markers">
                  <span className="marker">00:00</span>
                  <span className="marker">04:00</span>
                  <span className="marker">08:00</span>
                  <span className="marker">12:00</span>
                  <span className="marker">16:00</span>
                  <span className="marker">20:00</span>
                  <span className="marker">24:00</span>
                </div>

                {/* Waveform Visualization with Evidence Markers */}
                <div className="waveform-container">
                  <div className="waveform">
                    {waveform.map((height, index) => (
                      <div key={index} className="wave-bar" style={{ height: `${height}px` }} />
                    ))}
                  </div>
                  
                  {/* Evidence Markers */}
                  <div className="evidence-markers">
                    {evidenceMarkers.map((marker) => (
                      <div
                        key={marker.id}
                        className={`evidence-marker evidence-${marker.deviceType}`}
                        style={{
                          left: `${marker.startPercent}%`,
                          width: `${Math.max(marker.widthPercent, 0.5)}%`
                        }}
                        onClick={() => {
                          if (marker.videos) {
                            setSource(marker.videos);
                          }
                        }}
                      >
                        <div className="evidence-tooltip">
                          <div className="tooltip-content">
                            <div className="tooltip-header">
                              <span className="tooltip-type">{marker.deviceType}</span>
                              <span className="tooltip-device">({marker.device})</span>
                              <span className="tooltip-time">
                                {new Date(marker.startTime).toLocaleTimeString('en-US', { 
                                  hour12: false, 
                                  hour: '2-digit', 
                                  minute: '2-digit' 
                                })}
                              </span>
                            </div>
                            <div className="tooltip-details">
                              <span className="tooltip-original-type">Type: {marker.type}</span>
                            </div>
                            {marker.type === 'safety' && (
                              <div className="tooltip-safety">
                                <span className={marker.ppe ? 'safe' : 'unsafe'}>PPE {marker.ppe ? '✓' : '✗'}</span>
                                <span className={marker.shoes ? 'safe' : 'unsafe'}>Shoes {marker.shoes ? '✓' : '✗'}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Progress indicator */}
                  <div className="timeline-progress" style={{ left: `${progressPct}%` }} />
                </div>
              </div>
            </section>
          </div>
        </div>

        {/* Right side - Camera panel */}
        <div 
          className="scrollbar-thin"
          style={{ 
            flex: '0 0 auto', 
            width: '100%',
            maxWidth: window.innerWidth >= 1600 ? '480px' : 
                     window.innerWidth >= 1440 ? '420px' : 
                     window.innerWidth >= 1024 ? '340px' : '350px', 
            minWidth: window.innerWidth >= 1600 ? '420px' : 
                     window.innerWidth >= 1440 ? '360px' : 
                     window.innerWidth >= 1024 ? '300px' : '300px', 
            marginTop: '10px',
            overflowY: 'auto',
            maxHeight: 'calc(100vh - 120px)'
          }}
        >
          <RightBar videoSource={setSource} />
        </div>
      </div>
    </div>
  );
};

export default EvidencePage;
